// components/admin/dynamicCategory/DynamicCategoryManager.js
import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddDynamicCategory from "./AddDynamicCategory";
import { useStaticData } from "../../../CustomHooks/StaticListProvider"; // Custom hook for static data
import Loader from "../../loader/Loader";

const DynamicCategoryManager = () => {
  const {
    categories: { list: categories }, // Static categories for modal dropdown
    brands: { list: brands }, // Static brands for modal selection
    dynamicCategories: {
      categoryBrandMap, // The fetched data: { category: [brands], ... }
      loading: dynamicLoading, // Loading state for dynamic data (covers fetch and operations)
      error: dynamicError, // Error state for dynamic data
      deleteAllBrandsFromCategory,
      addBrand: addBrandToCategoryHook, // <-- Get the add function from the hook (used for saving and deleting all)
      deleteBrand: deleteBrandFromCategoryHook, // <-- Get the delete function for single brand from the hook
    },
  } = useStaticData();

  const [isModalVisible, setIsModalVisible] = useState(false); // For handling modal visibility

  const openModal = () => setIsModalVisible(true); // Open modal to add brands
  const closeModal = () => {
    setIsModalVisible(false);
  };

  // Function to handle brand deletion (using the hook's function)
  const handleDeleteBrand = async (category, brand) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete brand "${brand}" from category "${category}"?`
    );
    if (!confirmDelete) {
      return; // Stop if user cancels
    }

    try {
      await deleteBrandFromCategoryHook(category, brand);
      toast.success(
        `Brand "${brand}" deleted from "${category}" successfully!`
      );
    } catch (error) {
      console.error(
        `Failed to delete brand "${brand}" from "${category}":`,
        error
      );
      toast.error(`Failed to delete brand "${brand}": ${error.message}`);
    }
  };

  // --- NEW FUNCTION TO DELETE ALL BRANDS FOR A CATEGORY ---
  const handleDeleteAllBrandsFromCategory = async (category) => {
    const brandCount = categoryBrandMap[category]?.length || 0;
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ALL ${brandCount} brands from the category "${category}"? This action cannot be undone.`
    );

    if (!confirmDelete) {
      return; // Stop if user cancels
    }

    try {
      await deleteAllBrandsFromCategory(category);
      toast.success(`All brands deleted from "${category}" successfully!`);
    } catch (error) {
      console.error(`Failed to delete all brands from "${category}":`, error);
      toast.error(
        `Failed to delete all brands from "${category}": ${error.message}`
      );
    }
  };

  // ---------------------------------------------------------

  // Function to handle saving brands from the modal - INCLUDES THE CONFIRMATION LOGIC
  const handleSaveBrands = async (category, selectedBrands) => {
    if (!category || !selectedBrands) {
      // selectedBrands can be empty array
      toast.error("Internal error: Missing category or brands.");
      return;
    }

    const categoryExists =
      categoryBrandMap.hasOwnProperty(category) &&
      categoryBrandMap[category].length > 0;

    if (categoryExists) {
      // Compare current brands with selected brands to see if it's a significant change
      const currentBrands = categoryBrandMap[category] || [];
      const areBrandsDifferent =
        JSON.stringify([...currentBrands].sort()) !==
        JSON.stringify([...selectedBrands].sort());

      if (areBrandsDifferent) {
        const confirmOverwrite = window.confirm(
          `Category "${category}" already exists with brands (${
            currentBrands.join(", ") || "none"
          }). Do you want to replace them with your selection (${
            selectedBrands.join(", ") || "none"
          })?`
        );

        if (!confirmOverwrite) {
          toast.warning(`Brands for "${category}" were not updated.`);
          closeModal(); // Close the modal as the operation was cancelled
          return; // Exit if the user cancels the overwrite
        }
      } else {
        // No change in brands, just close the modal
        toast.info(`No changes made for category "${category}".`);
        closeModal();
        return;
      }
    }

    try {
      await addBrandToCategoryHook(category, selectedBrands);

      if (categoryExists) {
        toast.success(`Brands for "${category}" have been updated!`);
      } else {
        toast.success(`New category "${category}" created with brands!`);
      }

      closeModal(); // Close the modal on success
    } catch (error) {
      console.error("Error saving brands:", error);
      toast.error(`Failed to save brands: ${error.message}`);
    }
  };

  // Handle loading and error states from the hook
  if (dynamicLoading && Object.keys(categoryBrandMap).length === 0) {
    return <Loader />;
  }

  if (dynamicError && Object.keys(categoryBrandMap).length === 0) {
    return (
      <div className="text-center text-red-600">
        Error loading dynamic categories: {dynamicError}
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-center text-gray-600 leading-tight font-raleway mb-4">
        Manage Dynamic Categories and Brands
      </h1>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={openModal}
          className="bg-orange-600 text-white text-sm font-bodyFont px-4 py-2 hover:bg-orange-700 duration-300 font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={dynamicLoading} // Disable button while loading an operation
        >
          Add/Update Brands for a Category
        </button>
      </div>

      {/* Modal to add/update brands */}
      {isModalVisible && (
        <AddDynamicCategory
          onClose={closeModal}
          onBrandAdded={handleSaveBrands} // Pass the handler function
          categories={categories} // Pass static categories for dropdown
          brands={brands} // Pass static brands for selection (full list)
          categoryBrandMap={categoryBrandMap} // Pass the current map for potential filtering (optional for current modal logic)
          isLoading={dynamicLoading} // Pass loading state to modal
        />
      )}

      {/* Display current dynamic categories and brands */}
      {dynamicLoading && Object.keys(categoryBrandMap).length > 0 && (
        <div className="text-center text-gray-500 mt-4">Updating...</div>
      )}
      {dynamicError && Object.keys(categoryBrandMap).length > 0 && (
        <div className="text-center text-red-600 mt-4">
          Operation failed: {dynamicError}
        </div>
      )}

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-4">
        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3 w-1/3">
                Category
              </th>
              <th scope="col" className="px-6 py-3 w-2/3">
                Brands
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(categoryBrandMap).map((category) => (
              <tr
                key={category}
                className="odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800 border-b dark:border-gray-700"
              >
                <td className="px-6 py-4 align-top font-semibold flex items-center justify-between">
                  <span>{category}</span>
                  {/* --- NEW DELETE ALL BUTTON --- */}
                  <button
                    onClick={() => handleDeleteAllBrandsFromCategory(category)}
                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-xs ml-2 disabled:bg-red-300 disabled:cursor-not-allowed"
                    // Disable the button if there are no brands or while loading

                    title={
                      !categoryBrandMap[category] ||
                      categoryBrandMap[category].length === 0
                        ? "No brands to delete for this category"
                        : `Delete all brands for ${category}`
                    }
                  >
                    Delete All
                  </button>
                  {/* ------------------------------ */}
                </td>
                <td className="px-6 py-4">
                  {categoryBrandMap[category] &&
                  categoryBrandMap[category].length > 0 ? (
                    <ul className="list-disc list-inside">
                      {categoryBrandMap[category].map((brand) => (
                        <li
                          key={brand}
                          className="mb-1 flex justify-between items-center"
                        >
                          <span>{brand}</span>
                          <button
                            onClick={() => handleDeleteBrand(category, brand)}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-xs ml-2 disabled:bg-red-300 disabled:cursor-not-allowed"
                            disabled={dynamicLoading}
                          >
                            Delete
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    "No brands assigned"
                  )}
                </td>
              </tr>
            ))}
            {Object.keys(categoryBrandMap).length === 0 &&
              !dynamicLoading &&
              !dynamicError && (
                <tr>
                  <td
                    colSpan="2"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No dynamic categories or brands found. Click "Add/Update
                    Brands for a Category" to get started.
                  </td>
                </tr>
              )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DynamicCategoryManager;
